import { useMutation, useQueryClient } from "@tanstack/solid-query";
import { axiosInstance } from "@/api/client";
import {
  BILLING_USAGE_KEY,
  COMPANY_KEY,
  JOB_APPLICATION_KEY,
  JOB_OFFER_KEY,
  PROFILE_KEY,
} from "@/api/query-keys";

import type {
  CreateJobOfferV2RequestBody,
  CreateJobOfferV2Response,
} from "./types";

export const createJobOfferV2 = async (body: CreateJobOfferV2RequestBody) => {
  const { additionalDocuments, ...restBody } = body;

  // If there are additional documents, use FormData
  if (additionalDocuments && additionalDocuments.length > 0) {
    const formData = new FormData();

    // Add all regular fields to FormData
    formData.append("company", JSON.stringify(restBody.company));
    formData.append("job_offer", JSON.stringify(restBody.job_offer));
    if (restBody.options) {
      formData.append("options", JSON.stringify(restBody.options));
    }

    // Add additional documents with the correct field name
    additionalDocuments.forEach((file) => {
      formData.append("additionalDocuments", file);
    });

    const { data } = await axiosInstance.post<CreateJobOfferV2Response>(
      "/job-offer/v2",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return data;
  }

  // Otherwise, send as JSON
  const { data } = await axiosInstance.post<CreateJobOfferV2Response>(
    "/job-offer/v2",
    restBody,
  );
  return data;
};

export const useCreateJobOfferV2 = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: createJobOfferV2,
    onSuccess: () => {
      // Each key must be invalidated on its own — a single multi-element
      // queryKey is a prefix match that hits none of these caches. Includes
      // BILLING_USAGE_KEY so publishing a new offer updates the quota guard.
      for (const key of [
        JOB_OFFER_KEY,
        COMPANY_KEY,
        PROFILE_KEY,
        JOB_APPLICATION_KEY,
        BILLING_USAGE_KEY,
      ]) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  }));
};
